import { BoundingBoxWithHeight } from '@viewer3D/types';
import proj4 from 'proj4';
import { proxy } from 'valtio';

// TODO rename to GeodeticCoordinates
export interface LngLat {
  lat: number;
  // TODO refactor to "long" instead of "lng" because we use "long" on Photo-interface. Will reduce some unnecessary destructuring and will make code cleaner.
  lng: number;
}

// TODO rename to CartesianCoordinates
export interface Vector2d {
  x: number;
  y: number;
}

export const spatialRefState = proxy<{ lngLatToUTM: null | proj4.Converter }>({
  lngLatToUTM: null,
});

export const setProj4Projection = (epsgCode: string, projection: string): void => {
  proj4.defs(epsgCode, projection);
  spatialRefState.lngLatToUTM = proj4(`EPSG:4326`, epsgCode);
};

export const lngLatToXY = ({ lng, lat }: LngLat): Vector2d => {
  if (!spatialRefState.lngLatToUTM) {
    throw new Error('No projection set');
  }
  const [x, y] = spatialRefState.lngLatToUTM.forward([lng, lat]);
  return { x, y };
};

export const xyToLngLat = ({ x, y }: Vector2d): LngLat => {
  if (!spatialRefState.lngLatToUTM) {
    throw new Error('No projection set');
  }
  const [lng, lat] = spatialRefState.lngLatToUTM.inverse([x, y]);
  return { lng, lat };
};

const calculateMapboxZoom = (cameraViewport: BoundingBoxWithHeight): number => {
  if (!cameraViewport) {
    throw new Error('Camera viewport is required');
  }

  const { minX, maxX, minY, maxY } = cameraViewport;

  const width = Math.abs(maxX - minX);
  const height = Math.abs(maxY - minY);

  // Choose the larger dimension to ensure it fits within the viewport
  const viewportSize = Math.max(width, height);

  const TILE_SIZE = 512; // Default Mapbox tile size
  const EARTH_CIRCUMFERENCE = 40075016.686; // Earth's circumference in meters at the equator

  // The world size at zoom level 0 (one tile fits the whole world)
  const worldSizeAtZoom0 = TILE_SIZE;

  // Calibration factor to fine-tune the result
  // Adjust based on empirical testing
  const CALIBRATION_FACTOR = 180;

  // Scale to convert the viewport size to map units
  const scale = (viewportSize / EARTH_CIRCUMFERENCE) * CALIBRATION_FACTOR;

  // Calculate the zoom level based on the scale
  const zoom = Math.log2(worldSizeAtZoom0 / scale);

  return zoom;
};

/**
 * @example Translate Three.js zoom -> Mapbox zoom
 * 0.001 -> 7
 * 0.1 -> 12
 * 0.7 -> 15
 * 24 -> 20
 */
export const calculateThreeZoom = (mapboxZoom: number) => {
  // slightly less than 2, which dampens the growth.
  const EXPONENTIAL_BASE = 1.9;

  // Scale factor to fine-tune the result
  // Adjust based on empirical testing
  const SCALE_FACTOR = 0.0001;

  // Calculate the raw Three.js zoom value using the exponential formula
  const calculatedZoom = SCALE_FACTOR * Math.pow(EXPONENTIAL_BASE, mapboxZoom);

  // Clamp the result to stay within your required bounds
  const MIN_THREE_JS_ZOOM = 0.001;
  const MAX_THREE_JS_ZOOM = 24;
  const clampedZoom = Math.max(MIN_THREE_JS_ZOOM, Math.min(calculatedZoom, MAX_THREE_JS_ZOOM));

  return clampedZoom;
};

export const convertThreeViewportToMapbox = (cameraViewport: BoundingBoxWithHeight | undefined) => {
  if (!cameraViewport) {
    return {};
  }

  const { minX, minY, maxX, maxY } = cameraViewport;

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  const { lng, lat } = xyToLngLat({ x: centerX, y: centerY });

  const zoom = calculateMapboxZoom(cameraViewport);

  return {
    zoom: parseFloat(zoom.toFixed(2)),
    lat: parseFloat(lat.toFixed(4)),
    lon: parseFloat(lng.toFixed(4)),
  };
};

export const buildConMonLink = (zoom: number, lat: number, lon: number, isDev: boolean) => {
  const baseUrl = isDev ? 'https://app.test.deepup.io' : 'https://app.deepup.io';

  return `${baseUrl}/map#${zoom}/${lat}/${lon}`;
};
